import { api } from './api';

export type TemporaryAbsenceReason = 'hygiene' | 'botengang' | 'organisation' | 'support' | 'sonstiges';

export const ABSENCE_REASON_LABELS: Record<TemporaryAbsenceReason, string> = {
  hygiene: 'Hygiene',
  botengang: 'Botengang',
  organisation: 'Organisation',
  support: 'Support',
  sonstiges: 'Sonstiges',
};

export type ContextType = 'lernbuero_session' | 'teaching_unit';

export interface TemporaryAbsenceItem {
  id: number;
  context_type: ContextType;
  context_id: number;
  student_unid: string;
  reason: TemporaryAbsenceReason;
  started_at: string;
  ended_at: string | null;
  effective_ended_at: string | null;
  is_active: boolean;
}

export interface ActiveAbsenceMap {
  [student_unid: string]: {
    id: number;
    reason: TemporaryAbsenceReason;
    started_at: string;
  };
}

export async function fetchTemporaryAbsences(
  contextType: ContextType,
  contextId: number
): Promise<{ data: TemporaryAbsenceItem[] }> {
  const { data } = await api.get('/temporary-absences', {
    params: { context_type: contextType, context_id: contextId },
  });
  return data;
}

export async function fetchActiveAbsences(
  contextType: ContextType,
  contextId: number
): Promise<{ data: ActiveAbsenceMap }> {
  const { data } = await api.get('/temporary-absences/active', {
    params: { context_type: contextType, context_id: contextId },
  });
  return data;
}

export async function startTemporaryAbsence(
  contextType: ContextType,
  contextId: number,
  studentUnid: string
): Promise<{ data: TemporaryAbsenceItem }> {
  const { data } = await api.post('/temporary-absences/start', {
    context_type: contextType,
    context_id: contextId,
    student_unid: studentUnid,
  });
  return data;
}

export async function endTemporaryAbsence(
  contextType: ContextType,
  contextId: number,
  studentUnid: string
): Promise<void> {
  await api.post('/temporary-absences/end', {
    context_type: contextType,
    context_id: contextId,
    student_unid: studentUnid,
  });
}

function parseAbsenceTimestamp(value: string): number {
  const sqlLike = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/;
  const match = String(value || '').match(sqlLike);
  if (match) {
    const [, y, mo, d, h, mi, s] = match;
    const asLocalMs = new Date(
      Number(y),
      Number(mo) - 1,
      Number(d),
      Number(h),
      Number(mi),
      Number(s)
    ).getTime();
    const asUtcMs = Date.UTC(
      Number(y),
      Number(mo) - 1,
      Number(d),
      Number(h),
      Number(mi),
      Number(s)
    );
    // Some environments return SQL DATETIME in local time, others in UTC-like form.
    // Pick the interpretation that is closer to "now" to keep counters stable.
    const now = Date.now();
    const localDiff = Math.abs(now - asLocalMs);
    const utcDiff = Math.abs(now - asUtcMs);
    return localDiff <= utcDiff ? asLocalMs : asUtcMs;
  }
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

export function formatAbsenceDuration(startedAt: string): string {
  const start = parseAbsenceTimestamp(startedAt);
  const now = Date.now();
  const diffMs = Math.max(0, now - start);
  const minutes = Math.floor(diffMs / 60000);
  const seconds = Math.floor((diffMs % 60000) / 1000);
  if (minutes > 0) return `${minutes} Min`;
  return `${seconds} Sek`;
}
